// Single row in the sidebar agent list.
//
// Layout (D54, extended by sprint agent-stages):
//   [stage-bar] [status-icon]  Name                 timestamp
//                               subtitle (conditional)  hover: action buttons
//
// Subtitle visibility is state-driven AND recency-driven:
//   - Active states (running/waiting/error/stuck/interrupted) always show.
//   - finished: diff stat only when lastActivityAt is <24 h old. Fence
//     note: D63 scoped the diff stat to the Pinned + Today sections; the
//     stage redesign (spec agent-stages D3) retired those sections, so
//     the ORIGINAL intent — hide stale diff noise on old rows — is kept
//     via the same 24 h boundary the Today bucket used.
//
// Hover affordances (D40, D62, UI-8.2 Cursor-mirror; pin retired per
// spec agent-stages D3 — stages replace pinning, `pinned` survives at
// the data layer only):
//   - Active rows: Archive + Kebab on the right; left status-icon fixed.
//   - Archived rows: Restore / Delete on the right.
//
// Interactions:
//   - Click → select + mark read.
//   - Right-click / long-press → context menu.
//   - Double-click on name (desktop) → inline rename.
//
// DS.5 (sprint aos-ds-shell, spec §2 D3 / DS.4 D16 / AMENDMENT DS.5-B8): the
// row stays an As Oasis PRODUCT BLOCK — never the DS `List` (rows carry DnD
// between sections, a long-press menu, an unread dot, a status icon and a
// stage colour bar the piece has no slot for). Its internals draw DS pieces:
// `@ds/primitives/Icon` (hover actions, through the Lucide family),
// `@ds/data-display/Badge` (the unread dot), `@ds/data-display/Chip` (the
// "dispatched by" tag). The row's idle text reads `text-muted-foreground/75`
// — the skin's `--text-tertiary` derivation (64 % at 0.75, ≈ 50 % on the
// sidebar surface), replacing the raw 48.6 % grey hex Santi approved in chat
// 2026-05-12; the 1.4-point lightness step is the side-by-side's caption.

import { useEffect, useRef, useState } from 'react';
import type { Agent } from '../types';
import Badge from '@ds/data-display/Badge';
import Chip from '@ds/data-display/Chip';
import Icon from '@ds/primitives/Icon';
import { StatusIcon } from './StatusIcon';
import { useLongPress } from '../hooks/useLongPress';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useTranslation } from '../i18n';
import type { TKey } from '../i18n';
import { formatRelativeTime } from '../lib/time';

export interface AgentListItemProps {
  agent: Agent;
  selected: boolean;
  renaming: boolean;
  archived: boolean;
  // Sprint agent-stages — hex of the agent's stage color (resolved by the
  // Sidebar via paletteColor). null/undefined = no bar ("Sin estado").
  stageColorHex?: string | null;
  onSelect: () => void;
  onContextMenu: (point: { x: number; y: number }) => void;
  onRenameSubmit: (name: string) => void;
  onRenameCancel: () => void;
  onRenameStart?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}

const DIFF_STAT_RECENCY_MS = 24 * 60 * 60 * 1000;

// Sprint 12 (D-008) — binary Working / Idle. Only working and error
// show subtitles; finished shows git diff stats on recent rows.
// Sprint working-signal-ssot — keyed off the derived `working` signal;
// the reason-specific waits get founder copy instead of a stale tool
// summary (an agent waiting on a background subagent is not "Run tests").
function getSubtitle(
  agent: Agent,
  archived: boolean,
  t: (key: TKey, params?: Record<string, string | number>) => string,
): string | null {
  const { status } = agent;

  if (status === 'error') {
    return agent.errorMessage ?? t('status.error');
  }

  // Sprint job-tree-four-states — the rollup's own line wins when the
  // server sent one. It is already founder language (it prefers the CLI's
  // humanised tool description, never a tool id) and it is the only
  // source that knows about delegated children or a stalled turn.
  // `workingReason` below is the pre-sprint fallback for a payload from a
  // control plane that predates this.
  if (agent.founderStatus?.detail) return agent.founderStatus.detail;

  if (agent.working) {
    if (agent.workingReason === 'awaiting-permission') return t('workingReason.awaitingPermission');
    if (agent.workingReason === 'awaiting-task') return t('workingReason.awaitingTask');
    if (agent.workingReason === 'awaiting-wakeup') return t('workingReason.awaitingWakeup');
    return agent.lastAgentSummary ?? t('agentList.working');
  }

  if (status === 'finished') {
    const ts = Date.parse(agent.lastActivityAt);
    const isRecent =
      Number.isFinite(ts) && Date.now() - ts < DIFF_STAT_RECENCY_MS;
    if (!archived && isRecent) {
      const git = agent.gitStatus;
      if (git && git.filesChanged > 0) {
        if (git.linesRemoved === 0) {
          return t('agentList.diffStatNoRemoved', {
            added: git.linesAdded,
            files: git.filesChanged,
          });
        }
        return t('agentList.diffStat', {
          added: git.linesAdded,
          removed: git.linesRemoved,
          files: git.filesChanged,
        });
      }
      return t('agentList.done');
    }
    return null;
  }

  return null;
}

export function AgentListItem({
  agent,
  selected,
  renaming,
  archived,
  stageColorHex,
  onSelect,
  onContextMenu,
  onRenameSubmit,
  onRenameCancel,
  onRenameStart,
  onArchive,
  onRestore,
  onDelete,
}: AgentListItemProps) {
  const { t } = useTranslation();
  const longPress = useLongPress(onContextMenu, { delayMs: 500 });
  const isDesktop = useMediaQuery(MD_QUERY);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onContextMenu({ x: e.clientX, y: e.clientY });
  };

  const nameDoubleClick =
    isDesktop && onRenameStart
      ? (e: React.MouseEvent) => {
          e.stopPropagation();
          onRenameStart();
        }
      : undefined;

  const subtitle = getSubtitle(agent, archived, t);
  const hasSubtitle = subtitle !== null && !renaming;
  const isArchived = archived;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!renaming) onSelect();
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !renaming) {
          e.preventDefault();
          onSelect();
        }
      }}
      onContextMenu={handleContextMenu}
      onTouchStart={longPress.onTouchStart}
      onTouchMove={longPress.onTouchMove}
      onTouchEnd={longPress.onTouchEnd}
      onTouchCancel={longPress.onTouchCancel}
      data-testid={`agent-row-${agent.id}`}
      data-selected={selected ? 'true' : 'false'}
      data-pending={agent.pending ? 'true' : 'false'}
      className={[
        // Sprint `sidebar-row-alignment-and-color` (2026-05-12) —
        // `items-start` so icon + timestamp anchor to the top row (the
        // name line) when the row has a subtitle, instead of floating
        // between the two lines. Single-line rows look identical.
        'group flex items-start gap-gap-tight px-pad-chip-x cursor-pointer select-none',
        'transition-colors duration-motion-instant ease-cursor-out',
        hasSubtitle ? 'py-1.5' : 'py-2',
        // Santi (chat 2026-05-12) wants the idle row name and icon uniformly
        // darker than `text-foreground/74`, which felt too bright; selected
        // rows keep `text-foreground` so the selected state stands out.
        selected
          ? 'bg-secondary text-foreground'
          : 'hover:bg-secondary/40 text-muted-foreground/75',
        // Sprint 11b.3 (D-009 §3.5) — optimistic row treatment: 60%
        // opacity while `POST /api/agents/empty` is in flight. The
        // status icon's pulse comes from `data-pending=true` below.
        agent.pending ? 'opacity-60' : '',
      ].join(' ')}
    >
      {/* Sprint agent-stages — stage color bar. `w-0.5` from the standard
          Tailwind scale; hex comes from the palette map that backs the
          `avatar.palette-NN` tokens (same sanctioned pattern as the
          workspace avatars — see lib/avatar-defaults.ts header). The
          transparent placeholder keeps row alignment identical between
          staged and unstaged rows. */}
      <span
        className="w-0.5 self-stretch rounded-full shrink-0"
        style={stageColorHex ? { backgroundColor: stageColorHex } : undefined}
        aria-hidden="true"
        data-testid={stageColorHex ? `agent-stage-bar-${agent.id}` : undefined}
      />

      {/* Left status slot (Cursor-mirror). Fixed at btn-icon (22×22).
          Sprint agent-stages — the hover Pin swap is retired with the
          pin UI (spec D3); the status icon is now persistent. */}
      <span
        className={[
          'inline-flex h-btn-icon w-btn-icon items-center justify-center shrink-0',
          agent.pending ? 'animate-pulse' : '',
        ].join(' ')}
      >
        {/* Sprint job-tree-four-states — `founderStatus` is the server's
            rollup of the pending-job tree and wins when present; `status`
            and `working` remain as the fallback for a payload that
            predates it. */}
        <StatusIcon
          status={agent.status}
          working={agent.working}
          founderState={agent.founderStatus?.state}
          severity={agent.founderStatus?.severity}
          archived={isArchived}
        />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-gap-tight">
          {renaming ? (
            <RenameInput
              initial={agent.name}
              onSubmit={onRenameSubmit}
              onCancel={onRenameCancel}
            />
          ) : (
            <span
              className={[
                'truncate text-body',
                selected ? 'font-550 text-foreground' : 'font-450',
              ].join(' ')}
              data-testid={`agent-name-${agent.id}`}
              onDoubleClick={nameDoubleClick}
            >
              {agent.name}
            </span>
          )}
          {/* Sprint dispatch-lineage-ui (D2) — reachable ONLY through an
              explicit search (groupAgents hides dispatched workers
              otherwise): names the caller so the founder knows this is a
              machine's conversation, not one of his. */}
          {agent.dispatchedBy && !renaming && (
            <Chip
              variant="plain"
              size="xs"
              className="shrink-0"
              label={t('agentList.dispatchedBy', {
                name: agent.dispatchedBy.callerName ?? t('dispatchCard.unknownCaller'),
              })}
              data-testid={`agent-dispatched-badge-${agent.id}`}
            />
          )}
        </div>
        {hasSubtitle && (
          <p className="text-caption text-muted-foreground truncate mt-0.5">
            {subtitle}
          </p>
        )}
      </div>

      {/* Trailing: timestamp (default) vs action buttons (hover) */}
      {!renaming && (
        <div className="shrink-0 flex items-center">
          {/* Timestamp — hidden on hover (desktop) */}
          <span className="text-caption text-muted-foreground/70 group-hover:hidden">
            {formatRelativeTime(agent.lastActivityAt)}
          </span>

          {/* Hover actions — visible on hover only. Sprint UI-8.1 hits
              pixel parity with `docs/ui/mockups/01-dashboard.html`
              (`.agent-action`, L376-389): 22×22 hit area, 13 px icon,
              8 px gap between buttons, --fg-tertiary idle, MoreVertical
              for the third action. */}
          <div className="hidden group-hover:flex items-center gap-2">
            {isArchived ? (
              <ArchivedHoverButtons
                onRestore={onRestore}
                onDelete={onDelete}
              />
            ) : (
              <ActiveHoverButtons
                onArchive={onArchive}
                onContextMenu={(e) => {
                  e.stopPropagation();
                  onContextMenu({ x: e.clientX, y: e.clientY });
                }}
              />
            )}
          </div>

          {agent.unread && (
            <Badge
              variant="dot"
              color="secondary"
              standalone
              className="ml-1.5 shrink-0 group-hover:hidden"
              aria-label={t('agentList.unread')}
              data-testid={`agent-unread-${agent.id}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ActiveHoverButtons({
  onArchive,
  onContextMenu,
}: {
  onArchive?: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {onArchive && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onArchive(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.archive')}
          aria-label={t('tooltip.archive')}
        >
          <Icon name="archive" size={13} />
        </button>
      )}
      <button
        type="button"
        onClick={onContextMenu}
        className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
        title={t('tooltip.moreActions')}
        aria-label={t('tooltip.moreActions')}
      >
        <Icon name="more_vert" size={13} />
      </button>
    </>
  );
}

function ArchivedHoverButtons({
  onRestore,
  onDelete,
}: {
  onRestore?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {onRestore && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRestore(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.restore')}
          aria-label={t('tooltip.restore')}
        >
          <Icon name="restore" size={13} />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="h-btn-icon w-btn-icon min-h-btn-icon min-w-btn-icon inline-flex items-center justify-center rounded text-muted-foreground hover:text-status-error hover:bg-popover transition-colors duration-motion-instant"
          title={t('tooltip.deletePermanently')}
          aria-label={t('tooltip.deletePermanently')}
        >
          <Icon name="delete" size={13} />
        </button>
      )}
    </>
  );
}

function RenameInput({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          const trimmed = value.trim();
          if (trimmed) onSubmit(trimmed);
          else onCancel();
        } else if (e.key === 'Escape') {
          onCancel();
        }
      }}
      onBlur={() => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== initial) onSubmit(trimmed);
        else onCancel();
      }}
      className="flex-1 min-w-0 h-8 px-2 text-body bg-popover border border-border rounded text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
      data-testid="agent-rename-input"
      aria-label={t('agentList.renameAgent')}
    />
  );
}

